import { useState } from "react";
import { toLocalIsoDateString } from "../utils/formatDate";

import {
  getCurrentRoundTarget,
  incrementCounterProgress,
  decrementCounterProgress,
  createHistoryEntry,
  appendHistoryEntry,
  markCounterInProgress,
  completeCounterRound,
  clearCurrentCount,
} from "../utils/counterState";

export default function useCounterProgress({
  activeCounter,
  activeCounterId,
  isRunning,
  setIsRunning,
  setCounters,
}) {
  const [showRoundComplete, setShowRoundComplete] = useState(false);
  const [goalComplete, setGoalComplete] = useState(false);

  // goal intermediate round type
  const [goalRoundKind, setGoalRoundKind] = useState(null);

  // -------------------------
  // HELPERS
  // -------------------------
  const logHistory = (counterId, count, spentTime) => {
    const entry = createHistoryEntry(new Date(), count, spentTime);
    setCounters((prev) => appendHistoryEntry(prev, counterId, entry));
  };

  // -------------------------
  // INCREMENT
  // -------------------------
  const handleIncrement = () => {
    if (!activeCounter) return;

    if (!isRunning) setIsRunning(true);

    // mark in progress
    if (
      activeCounter.currentCount === 0 &&
      activeCounter.status === "Pending"
    ) {
      setCounters((prev) =>
        markCounterInProgress(prev, activeCounterId)
      );
    }

    const completedCount = activeCounter.completedCount || 0;
    const nextCompletedCount = completedCount + 1;
    const nextCurrentCount = activeCounter.currentCount + 1;

    const currentRoundTarget =
      getCurrentRoundTarget(activeCounter);

    const goalLimit = Number(activeCounter.limit) || 0;

    // -------------------------
    // GOAL COMPLETED
    // -------------------------
    if (
      activeCounter.type === "goal" &&
      goalLimit > 0 &&
      nextCompletedCount >= goalLimit
    ) {
      const today = toLocalIsoDateString();

      logHistory(
        activeCounter.id,
        nextCurrentCount,
        activeCounter.spentTime
      );

      setGoalRoundKind(null);

      setCounters((prev) =>
        completeCounterRound(prev, activeCounterId, {
          completedCount: nextCompletedCount,
          status: "Completed",
          isDisabled: true,
          lastGoalRoundCompleteDate: today,
        })
      );

      setIsRunning(false);
      setShowRoundComplete(true);
      setGoalComplete(true);
      return;
    }

    // -------------------------
    // GOAL ROUND COMPLETE
    // -------------------------
    if (
      activeCounter.type === "goal" &&
      nextCurrentCount >= currentRoundTarget
    ) {
      const today = toLocalIsoDateString();

      const sameCalendarDayExtraRound =
        activeCounter.lastGoalRoundCompleteDate === today;

      setGoalRoundKind(
        sameCalendarDayExtraRound
          ? "extraToday"
          : "firstToday"
      );

      logHistory(
        activeCounter.id,
        currentRoundTarget,
        activeCounter.spentTime
      );

      setCounters((prev) =>
        completeCounterRound(prev, activeCounterId, {
          completedCount: nextCompletedCount,
          lastGoalRoundCompleteDate: today,
        })
      );

      setGoalComplete(false);
      setShowRoundComplete(true);
      setIsRunning(false);
      return;
    }

    // -------------------------
    // DAILY ROUND COMPLETE
    // -------------------------
    if (
      activeCounter.type === "daily" &&
      nextCurrentCount >= activeCounter.perDayCount
    ) {
      logHistory(
        activeCounter.id,
        activeCounter.perDayCount,
        activeCounter.spentTime
      );

      setCounters((prev) =>
        completeCounterRound(prev, activeCounterId, {
          completedCount: nextCompletedCount,
        })
      );

      setShowRoundComplete(true);
      setIsRunning(false);
      return;
    }

    // -------------------------
    // NORMAL INCREMENT
    // -------------------------
    setCounters((prev) =>
      incrementCounterProgress(prev, activeCounterId)
    );
  };

  // -------------------------
  // DECREMENT
  // -------------------------
  const handleDecrement = () => {
    if (
      !activeCounter ||
      activeCounter.isDisabled ||
      activeCounter.currentCount < 1
    ) {
      return;
    }

    setCounters((prev) =>
      decrementCounterProgress(prev, activeCounterId)
    );
  };

  // -------------------------
  // ROUND CONTROLS
  // -------------------------
  const handleRoundStop = () => {
    setShowRoundComplete(false);
    setGoalRoundKind(null);
    setIsRunning(false);
  };

  const handleRoundContinue = () => {
    setCounters((prev) =>
      clearCurrentCount(prev, activeCounterId)
    );

    setShowRoundComplete(false);
    setGoalRoundKind(null);
    setIsRunning(true);
  };

  // -------------------------
  // RETURN
  // -------------------------
  return {
    showRoundComplete,
    goalComplete,
    goalRoundKind,
    logHistory,
    setGoalComplete,
    handleIncrement,
    handleDecrement,
    handleRoundStop,
    handleRoundContinue,
  };
}